import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';
import { Modal as FlowbiteModal } from 'flowbite';

import UsersCreate from '../../admin/UsersCreate';
import SupportUsers from '../../support_users/SupportUsers';

import { PER_PAGE_OPTIONS, queryParams } from './organisationUserFormat';
import OrganisationUserPanel from './OrganisationUserPanel';
import OrganisationUserTable from './OrganisationUserTable';
import OrganisationUserTiles from './OrganisationUserTiles';
import OrganisationUserToolbar from './OrganisationUserToolbar';
import useOrganisationUserActions from './useOrganisationUserActions';
import useOrganisationUserList from './useOrganisationUserList';

import { modalOptions } from 'Helpers/modalOptions';

import Alert from 'CommonComponents/Alert';
import Button from 'CommonComponents/Button';
import PaginationInfo from 'CommonComponents/PaginationInfo';
import PerPageSelect from 'CommonComponents/PerPageSelect';
import TablePagination from 'CommonComponents/TablePagination';
import Tabs from 'CommonComponents/Tabs';

import { ConfirmationModalProvider } from 'Context/ConfirmationModalContext';

function MfaRequiredBanner() {
    return (
        <p className="tw-mb-0 tw-rounded-lg tw-bg-blue-15 tw-px-4 tw-py-3 tw-text-xs tw-text-charcoal-85" role="status">
            {$t(
                'Multi-factor authentication is required for this organisation (set in Settings). The MFA column shows whether each user has completed set-up; users who have not will be prompted at their next login.',
            )}
        </p>
    );
}

function UsersTab({ urls, initialQuery, projects, mfaRequired, canManage, currentUserId }) {
    const list = useOrganisationUserList({ listUrl: urls.list, listSettingsUrl: urls.listSettings, initialQuery });
    const { query, setQuery, meta, rows, refresh } = list;

    const [panelUserId, setPanelUserId] = useState(null);
    const [panelUser, setPanelUser] = useState(null);
    const [roles, setRoles] = useState([]);
    const [createModal, setCreateModal] = useState(null);
    const createModalRef = useRef();

    useEffect(() => {
        if (!canManage) {
            return;
        }

        setCreateModal(new FlowbiteModal(createModalRef.current, modalOptions(false)));
        axios
            .get(urls.roles)
            .then(({ data }) => setRoles(data.roles))
            .catch(() => {});
    }, [canManage, urls.roles]);

    const onActionDone = useCallback(
        (user) => {
            setPanelUser((current) => (current && current.id === user.id ? user : current));
            refresh();
        },
        [refresh],
    );

    const actions = useOrganisationUserActions({ usersUrl: urls.users, onDone: onActionDone });

    const openPanel = useCallback((row) => {
        setPanelUser(null);
        setPanelUserId(row.id);
    }, []);

    const download = useCallback(() => {
        const params = new URLSearchParams(queryParams(query)).toString();

        window.location.assign(params ? `${urls.export}?${params}` : urls.export);
    }, [urls.export, query]);

    return (
        <div className="table__card tw-space-y-4">
            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-4">
                <h2 className="table__header tw-mb-0">{$t('Users')}</h2>
                {canManage && (
                    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
                        <Button variant="outline" onClick={download}>
                            {$t('Download list')}
                        </Button>
                        <Button variant="primary" onClick={() => createModal?.show()}>
                            {$t('Add new user')}
                        </Button>
                    </div>
                )}
            </div>

            {list.error && <Alert type="error">{list.error}</Alert>}

            <OrganisationUserTiles tiles={meta?.tiles} />

            {(meta?.mfa_required ?? mfaRequired) && <MfaRequiredBanner />}

            <OrganisationUserToolbar
                query={query}
                searchInput={list.searchInput}
                projects={projects}
                shown={meta?.total}
                total={meta?.total_members}
                onSearchChange={list.setSearchInput}
                onChange={setQuery}
                onClear={list.clear}
            />

            {list.loading && !meta ? (
                <p className="tw-text-sm tw-text-charcoal-75 tw-mb-0">{$t('Loading')}…</p>
            ) : (
                <OrganisationUserTable
                    rows={rows}
                    sort={query.sort}
                    direction={query.direction}
                    canManage={canManage}
                    currentUserId={currentUserId}
                    onSort={(sort, direction) => setQuery({ sort, direction })}
                    onOpen={openPanel}
                    actions={{
                        onEdit: openPanel,
                        onResend: actions.resendInvite,
                        onDeactivate: actions.deactivate,
                        onReactivate: actions.reactivate,
                    }}
                />
            )}

            {meta && (
                <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
                    <PerPageSelect
                        value={query.per_page}
                        options={PER_PAGE_OPTIONS}
                        label={$t('Rows per page')}
                        onChange={(event) => setQuery({ per_page: Number(event.target.value) })}
                    />
                    <div className="tw-flex tw-items-center tw-gap-3">
                        <PaginationInfo from={meta.from} to={meta.to} total={meta.total} />
                        {meta.last_page > 1 && (
                            <TablePagination
                                lastPage={meta.last_page}
                                currentPage={meta.current_page}
                                pageChange={({ selected }) => list.setPage(selected + 1)}
                            />
                        )}
                    </div>
                </div>
            )}

            <OrganisationUserPanel
                usersUrl={urls.users}
                userId={panelUserId}
                user={panelUser}
                onLoaded={setPanelUser}
                onSaved={onActionDone}
                onClose={() => setPanelUserId(null)}
                actions={actions}
            />

            {canManage && (
                <UsersCreate
                    modalId="create-user"
                    modalRef={createModalRef}
                    modalElement={createModal}
                    roles={roles}
                    departments={[]}
                    costcenters={[]}
                    revDepartments={[]}
                    revCostcenters={[]}
                    venues={[]}
                    fetchUsersHandler={refresh}
                    storeUserUrl={urls.store}
                    canCreate={canManage}
                    type={'Organisation'}
                />
            )}
        </div>
    );
}

const TAB_BAR = [
    '[&>div>.in-nav]:!tw-w-fit',
    '[&>div>.in-nav]:!tw-rounded-xl',
    '[&>div>.in-nav>[role=tab]]:!tw-h-[30px]',
    '[&>div>.in-nav>[role=tab]]:!tw-text-0.5sm',
    '[&>div>.in-nav>[role=tab]]:!tw-font-medium',
].join(' ');

export default function OrganisationUsersPage(props) {
    const { urls, isSuperAdmin } = props;

    // The design's tab card hugs its two tabs, which are a step smaller than
    // the shared full-width bar.
    return (
        <div className={TAB_BAR}>
            <Tabs
                renderStrategy="on-first-visit"
                tabs={[
                    { id: 'org-users', title: $t('Users'), content: () => <UsersTab {...props} /> },
                    {
                        id: 'org-support-users',
                        title: $t('Support users'),
                        content: () => (
                            <SupportUsers
                                listRolesUrl={urls.supportRoles}
                                usersUrl={urls.supportUsers}
                                inviteUrl={urls.inviteSupportUser}
                                revokeUrl={urls.revokeSupportUser}
                                canCreate={isSuperAdmin}
                                type="organisation"
                            />
                        ),
                    },
                ]}
            />
        </div>
    );
}

if (document.getElementById('OrganisationUsers')) {
    const rootEl = document.getElementById('OrganisationUsers');
    const json = (name) => JSON.parse(rootEl.getAttribute(name));

    ReactDOM.render(
        <App>
            {/* Above the user panel, which the confirmations open from. */}
            <ConfirmationModalProvider modalIndexClass="tw-z-[110]">
                <OrganisationUsersPage
                    urls={json('data-urls')}
                    initialQuery={json('data-initial-query')}
                    projects={json('data-projects')}
                    mfaRequired={json('data-mfa-required')}
                    canManage={json('data-can-manage')}
                    isSuperAdmin={json('data-is-super-admin')}
                    currentUserId={json('data-current-user-id')}
                />
            </ConfirmationModalProvider>
        </App>,
        rootEl,
    );
}
